import { ZIndex } from "@/domains/design/constants";
import { css, cx } from "@/domains/emotion";
import { AnimatePresence, motion } from "motion/react";
import { memo, type PropsWithChildren } from "react";

export interface FloatingActionsProps extends PropsWithChildren {
  className?: string;
  isOpen: boolean;
  verticalOffset?: number;
  bottom?: number | string;
}

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const FloatingActions = memo<FloatingActionsProps>(function FloatingActions({
  verticalOffset = 0,
  bottom,
  isOpen,
  children,
  className,
}) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className={cx(wrapperStyles, className)}
          initial={{ bottom: bottom ?? -65 }}
          animate={{ bottom: bottom ?? 10 + verticalOffset }}
          exit={{ bottom: bottom ?? -65 }}
          transition={{
            duration: 0.2,
            // cubic-bezier
            ease: [0.34, 1.56, 0.64, 1],
          }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
});

const wrapperStyles = css({
  display: "flex",
  flexDirection: "column",
  flex: 1,
  justifyContent: "space-around",
  alignItems: "center",
  position: "fixed",
  zIndex: ZIndex.FloatingActions,
  // Center horizontally without blocking clicks on the sides
  width: "fit-content",
  left: 0,
  right: 0,
  marginInline: "auto",
  boxSizing: "border-box",
  maxWidth: "100%",
});
